import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"

import { useUpdateSalesOrder } from "./useUpdateSalesOrder"

import type { SalesOrder } from "@/graphql/codegen/graphql"
import { type SalesOrderStatusFormValues, salesOrderStatusFormSchema } from "@/screens/CustomerOrders/schemas"

interface UseSalesOrderStatusFormProps {
  salesOrder: SalesOrder
  onClose: () => void
  onSubmit: () => void
}

interface UseSalesOrderStatusFormReturn {
  methods: UseFormReturn<SalesOrderStatusFormValues>
  handleSubmit: () => void
  handleCancel: () => void
  canSubmit: boolean
  isCancelConfirmationOpen: boolean
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isSaving: boolean
}

export const useSalesOrderStatusForm = ({
  salesOrder,
  onClose,
  onSubmit,
}: UseSalesOrderStatusFormProps): UseSalesOrderStatusFormReturn => {
  const methods = useForm<SalesOrderStatusFormValues>({
    defaultValues: { status: salesOrder.status },
    resolver: zodResolver(salesOrderStatusFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { updateSalesOrder, isSaving } = useUpdateSalesOrder()

  const handleSubmit = methods.handleSubmit(async (formValues) => {
    const savedSalesOrder = await updateSalesOrder(
      { salesOrderId: salesOrder.salesOrderId, status: formValues.status },
      { successMessage: "Status updated" }
    )

    if (savedSalesOrder) onSubmit()
  })

  const handleCancel = () => {
    if (isSaving) return
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
      return
    }
    onClose()
  }

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    methods,
    handleSubmit,
    handleCancel,
    canSubmit,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
    isSaving,
  }
}
